import { DisputeIcon } from "@xola/icons";
import { Checkbox, flash, formatDate, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { AxiosRequestConfig } from "axios";
import clsx from "clsx";
import { DateFormat } from "common";
import { inRange, isEmpty, isEqual } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Event, Guide, Timeslot } from "types";
import {
    ConfirmOverlap,
    getAssignedEvents,
    getAssignedGuides,
    getCreateEventPayload,
    getErrorMessage,
    getEventGuideKey,
    getGuideStatus,
    getOverlapError,
    GuideLabel,
    GuideStatus,
    isGuideAvailable,
    isGuideEnabledAndOverlapping,
    NoEventsMessage,
    NoSearchResultsMessage,
} from "./AssignGuidePopover";
import { api } from "@/api/api";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SearchInput } from "@/components/Common/SearchInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { TableSkeleton } from "@/components/Common/Skeleton/TableSkeleton";
import { TimeslotParams, UpdateParams, updateTimeslotCache } from "@/components/Event/EditEventCapacityModal";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { stringIncludes } from "@/hooks/useSearch";
import { useSelection } from "@/hooks/useSelection";
import { useGuides } from "@/hooks/useSellerResource";
import { needForceConfirm } from "@/models/Guide";
import { callDebounced } from "@/utils/debounceUtils";
import { sortBy } from "@/utils/locale";

interface AssignGuideModalProps {
    events?: Event[];
    timeslots: Timeslot[];
    isVisible: boolean;
    onClose: () => void;
}

export const AssignGuideModal = ({ events, timeslots, isVisible, onClose }: AssignGuideModalProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [isHideUnavailable, setIsHideUnavailable] = useState(false);
    const [partiallySelectedGuideId, setPartiallySelectedGuideId] = useState<ID | null>(null);
    const [forceConfirmGuides, setForceConfirmGuides] = useState<ID[]>([]);
    const [timeslotsToUpdate, setTimeslotsToUpdate] = useState<
        Array<{ timeslotParams: TimeslotParams; updateParams: UpdateParams }>
    >([]);

    const assignGuidesInFlight = useRef<Set<string>>(new Set());
    const unassignGuidesInFlight = useRef<Set<string>>(new Set());

    useEffect(() => {
        const assignGuidesInFlightCurrent = assignGuidesInFlight.current;
        const unassignGuidesInFlightCurrent = unassignGuidesInFlight.current;

        return () => {
            assignGuidesInFlightCurrent.clear();
            unassignGuidesInFlightCurrent.clear();
        };
    }, []);

    const getParams = () => {
        const params: any = {};

        if (isVisible) {
            if (events && events.length > 0) {
                params.events = events.map((event) => event.id).join(",");
            }

            for (const [index, timeslot] of timeslots.entries()) {
                if (!timeslot.arrivalDatetime) continue;

                params[`intervals[${index}][start]`] = formatDate(
                    timeslot.arrivalDatetime,
                    DateFormat.DATE_TIME_UTC_ISO,
                );
                params[`intervals[${index}][end]`] = formatDate(timeslot.end, DateFormat.DATE_TIME_UTC_ISO);
            }
        }

        return isEmpty(params) ? null : params;
    };

    const { guides, isLoadingGuides } = useGuides(getParams(), { enabled: isVisible });

    const sortedGuides = useMemo(() => {
        const filteredGuides = isHideUnavailable
            ? guides.filter((guide) => guide?.availability?.available === true)
            : [...guides];
        return sortBy(filteredGuides, "name");
    }, [guides, isHideUnavailable]);

    const assignedGuides = useMemo(() => (events ? getAssignedGuides(events) : []), [events]);
    const isGuideEnabled = (guide: Guide) => {
        return isGuideAvailable(guide) || assignedGuides.includes(guide.id) || isGuideEnabledAndOverlapping(guide);
    };

    const filteredGuides = searchTerm
        ? sortedGuides.filter((guide) => stringIncludes(searchTerm, guide.name))
        : sortedGuides;

    const selection = useSelection(filteredGuides, true, { all: false, selected: [] }, false);

    useEffect(() => {
        setSearchTerm("");
        selection.setState({ all: false, selected: assignedGuides });
        assignGuidesInFlight.current.clear();
        unassignGuidesInFlight.current.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const selectedGuides = guides.filter((guide) => {
        const alreadyAssigned = assignedGuides.includes(guide.id);
        if (alreadyAssigned) {
            const assignedEvents = events ? getAssignedEvents(events, guide.id) : [];
            for (const event of assignedEvents) {
                assignGuidesInFlight.current.delete(getEventGuideKey(event, guide));
            }
        }

        return selection.isSelected(guide) && !alreadyAssigned;
    });

    const unselectedGuides = guides.filter((guide) => {
        const alreadyAssigned = assignedGuides.includes(guide.id);
        return alreadyAssigned && !selection.isSelected(guide);
    });

    const handleSearch = (text: string): void => {
        setSearchTerm(text.trim());
    };

    const handleChangeHideUnavailable = () => {
        setIsHideUnavailable((previous) => !previous);
    };

    const handleRefetch = async (event?: Event) => {
        invalidate("events", event?.id);
        invalidate("guides");
    };

    const assignGuides = async (event: Event) => {
        const errorMessages = [];
        for (const guide of selectedGuides) {
            const eventGuideKey = getEventGuideKey(event, guide);
            if (assignGuidesInFlight.current.has(eventGuideKey)) continue;

            assignGuidesInFlight.current.add(eventGuideKey);
            const guideName = guide.name?.trim();
            try {
                await api.post(
                    `/events/${event.id}/guides`,
                    { guide: { ...guide, forceConfirm: forceConfirmGuides.includes(guide.id) } },
                    {
                        skipResponseInterceptor: true,
                        validateStatus: (status) => inRange(status, 200, 300) || status === 409,
                    } as AxiosRequestConfig,
                );
            } catch (error: any) {
                const message = getErrorMessage(error) ?? "";
                errorMessages.push(`Error assigning guide ${guideName} ${message}`.trim());
            }
        }

        if (errorMessages.length > 0) {
            flash.show({
                text: errorMessages.join("\n"),
                color: "danger",
                duration: 5000,
            });
        }

        if (selectedGuides.length > 0) {
            await handleRefetch(event);
        }
    };

    const unassignGuides = async (event: Event) => {
        const errorMessages = [];
        for (const guide of unselectedGuides) {
            if (unassignGuidesInFlight.current.has(guide.id)) continue;

            unassignGuidesInFlight.current.add(guide.id);
            try {
                await api.delete(`/events/${event.id}/guides/${guide.id}`, {
                    validateStatus: (status) => inRange(status, 200, 300) || status === 404,
                });
            } catch (error: any) {
                const message = getErrorMessage(error) ?? "";
                errorMessages.push(`Error removing guide ${guide.name?.trim()} ${message}`.trim());
            }

            unassignGuidesInFlight.current.delete(guide.id);
        }

        if (errorMessages.length > 0) {
            flash.show({
                text: errorMessages.join("\n"),
                color: "danger",
                duration: 5000,
            });
        }

        if (unselectedGuides.length > 0) {
            await handleRefetch(event);
        }
    };

    const { mutateAsync: updateEventGuideForAEvent, isLoading: isUpdating } = useMutation(
        async ({ event }: { event?: Event }) => {
            if (event) {
                const timeslotParams = {
                    timeslot: null,
                    event: event,
                    productId: null,
                    startDate: null,
                };

                const updateParams = {
                    isGranted,
                };

                await assignGuides(event);
                await unassignGuides(event);

                setTimeslotsToUpdate((values) => [
                    ...values.filter((v) => v.timeslotParams.event?.id !== timeslotParams.event?.id),
                    { timeslotParams, updateParams },
                ]);
            }
        },
    );

    const { mutateAsync: createEvent, isLoading: isCreatingEvent } = useMutation(
        async ({ timeslot }: { timeslot: Timeslot }) => {
            const updatedGuideList = guides.filter((guide) => selection.isSelected(guide));
            try {
                for (const guide of updatedGuideList) {
                    assignGuidesInFlight.current.add(guide.id);
                }

                const { data: event } = await api.post<Event>(
                    "/events",
                    getCreateEventPayload(timeslot, updatedGuideList),
                    { skipResponseInterceptor: true } as AxiosRequestConfig,
                );
                return { event };
            } catch (error: any) {
                const message = getErrorMessage(error) ?? "";
                flash.show({
                    text: `Failed to assign ${pluralize("guide", updatedGuideList.length)}. ${message}`.trim(),
                    color: "danger",
                    duration: 5000,
                });

                throw error instanceof Error ? error : new Error(error);
            }
        },
    );

    const updateEventGuides = async () => {
        if (isEqual(selection.state.selected, assignedGuides)) {
            onClose();
            return;
        }

        try {
            if (events && events?.length > 0) {
                for (const event of events) {
                    await updateEventGuideForAEvent({ event });
                }
            }

            if (timeslots?.length > 0) {
                for (const timeslot of timeslots) {
                    const { event } = (await createEvent({ timeslot })) ?? {};
                    if (event && timeslots.length === 1) {
                        const timeslotParams = {
                            timeslot,
                            event,
                            productId: timeslot.product,
                            startDate: formatDate(timeslot.arrivalDatetime),
                        };

                        const updateParams = {
                            isGranted,
                            forceReplaceId: timeslot.id,
                        };
                        await updateTimeslotCache(timeslotParams, updateParams);
                    }
                }

                await handleRefetch();
            }

            if (timeslotsToUpdate.length > 0) {
                for (const slot of timeslotsToUpdate) {
                    await updateTimeslotCache(slot.timeslotParams, slot.updateParams);
                }
            }

            onClose();
        } catch (error) {
            console.error("Failed to update guides:", error);
        }
    };

    const setConfirmationForGuide = (selectedGuide: Guide) => {
        selection.select(selectedGuide);
        setForceConfirmGuides([...forceConfirmGuides, selectedGuide.id]);
        setPartiallySelectedGuideId(null);
    };

    const isGuidePartiallySelected = (guide: Guide) => {
        return partiallySelectedGuideId === guide.id && !selection.isSelected(guide);
    };

    return (
        <Modal isOpen={isVisible} onClose={onClose}>
            <Modal.Header>Assign Guides</Modal.Header>

            <Modal.Body>
                <div className="my-3.5 flex justify-end">
                    <div className="flex items-center rounded-xl bg-gray-light py-0.5 px-3 text-sm">
                        {selection.state.all ? "All" : <NoTranslate>{selection.state.selected.length}</NoTranslate>}
                        &nbsp;Selected
                    </div>
                </div>
                <div className="mb-4 flex flex-col gap-2">
                    <SearchInput
                        value={searchTerm}
                        placeholder="Search guides..."
                        onChange={(searchResult) => callDebounced(300, handleSearch, searchResult)}
                    />

                    <div className="ml-2 mt-1 flex items-center px-4 text-sm font-normal">
                        <Checkbox
                            checked={isHideUnavailable}
                            label="Hide unavailable guides"
                            onChange={handleChangeHideUnavailable}
                        />
                    </div>
                </div>

                <div className="mx-4 mb-4 max-h-[60vh]">
                    {isLoadingGuides ? (
                        <div className="p-4">
                            <TableSkeleton rows={6} columns={2} className="pb-0" />
                        </div>
                    ) : (
                        <>
                            {events && events.length === 0 && <NoEventsMessage />}
                            {filteredGuides.length === 0 && <NoSearchResultsMessage />}

                            {filteredGuides.map((guide) => {
                                const status =
                                    events && events.length === 1 ? getGuideStatus(events[0].guides, guide) : undefined;
                                const overlapError = getOverlapError(guide);
                                const isSelected = selection.isSelected(guide);
                                const isUpdatingGuide = isUpdating || isCreatingEvent;
                                const isAvailable = guide?.availability?.available;
                                const needsConfirmation = isGuidePartiallySelected(guide) && needForceConfirm(guide);

                                return (
                                    <div
                                        key={guide.id}
                                        className={clsx(
                                            !isAvailable && "text-gray",
                                            "flex h-12 items-center border-b border-gray-lighter font-bold",
                                        )}
                                    >
                                        <div
                                            className="ml-2 flex flex-1 items-center"
                                            data-testid="filter-criteria-filter-item"
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                disabled={!isGuideEnabled(guide)}
                                                className="mr-3"
                                                onChange={() => {
                                                    if (isGuideEnabledAndOverlapping(guide)) {
                                                        setPartiallySelectedGuideId(guide.id);
                                                    } else {
                                                        selection.select(guide);
                                                    }
                                                }}
                                            />
                                            <GuideLabel guide={guide} isLoading={isLoadingGuides || isUpdatingGuide} />
                                        </div>

                                        {isGuideEnabledAndOverlapping(guide) ? (
                                            needsConfirmation ? (
                                                <ConfirmOverlap
                                                    onConfirm={() => setConfirmationForGuide(guide)}
                                                    onCancel={() => setPartiallySelectedGuideId(null)}
                                                />
                                            ) : (
                                                <Tooltip content={overlapError?.reason} placement="left">
                                                    <DisputeIcon size="large" className="text-warning-dark" />
                                                </Tooltip>
                                            )
                                        ) : (
                                            <GuideStatus status={status} isUpdating={isUpdatingGuide} />
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <SubmitButton
                    isLoading={isUpdating || isCreatingEvent}
                    disabled={isEqual(selection.state.selected, assignedGuides)}
                    onClick={updateEventGuides}
                >
                    Apply Changes
                </SubmitButton>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            </Modal.Footer>
        </Modal>
    );
};
